import {formatNumber} from '@angular/common';
import {Injectable, Input, OnDestroy} from '@angular/core';
import {BehaviorSubject, Subject, takeUntil} from 'rxjs';
import {AuthService} from '../../../../auth/auth.service';
import {ChartOptions} from '../services/estates-chart.service';
import {SeriesLabel} from '../services/estates.service';
import {TranslateService} from '@ngx-translate/core';
import {EstatesParametersService} from '../services/estates-parameters.service';
import {kfpColors} from '../../../../../../../../apps/fuse/src/assets/colors';

@Injectable({
    providedIn: 'root',
})
// tab 4 - Vývoj investice v čase
export class InvestmentEvaluationChart implements OnDestroy {
    public chart!: ChartOptions;
    logo?: string;
    series?: SeriesLabel[];
    @Input() height?: number;

    private dataChart = new BehaviorSubject<ChartOptions | null>(null);
    private destroy$ = new Subject<void>();

    data$ = this.dataChart.asObservable();
    year = 10;
    private absoluteSeries: any

    constructor(private authService: AuthService,
                private translateService: TranslateService,
                private _estatesParametersService: EstatesParametersService) {

        this.updateChartHeight();
        this._estatesParametersService.selectedYear$.pipe(takeUntil(this.destroy$)).subscribe((selectedYear) => {
            this.changeYear(selectedYear);
            this.year = selectedYear;
        });

        this.translateService
            .stream([
                'estates.calculation.params.estateValue',
                'estates.calculation.chart.to_pay',
                'estates.calculation.chart.sum_estate',
            ])
            .pipe(takeUntil(this.destroy$))
            .subscribe((keys) => {
                this.logo = this.authService.getLogoPath();
                this.dataChart.next({
                    series: [],
                    chart: {
                        background:
                            'linear-gradient(rgba(255,255,255,0.8), rgba(255,255,255,0.8)), url(/' + this.logo + ') no-repeat center; background-size: auto 150px, 150px auto; ',
                        type: 'bar',
                        height: this.height,
                        width: '100%',
                        stacked: true,
                        toolbar: {
                            tools: {
                                download: false,
                                selection: true,
                                zoom: true, // icon hidden, styles.scss
                                zoomin: false,
                                zoomout: false,
                                pan: true,
                                reset: true,
                            },
                            autoSelected: 'zoom',
                        },
                    },
                    plotOptions: {
                        bar: {
                            horizontal: true,
                            distributed: false,
                            rangeBarOverlap: true,
                            dataLabels: {
                                position: 'top',
                                hideOverflowingLabels: false,
                            },
                        },
                    },
                    colors: [
                        ({value, seriesIndex}) => {

                            switch (seriesIndex) {
                                case 0:
                                    return kfpColors.kfpEstatePrice;
                                case 1:
                                    return kfpColors.kfpHypoDefault;
                                case 2:
                                    return value < 0 ? kfpColors.kfpProfitMinus : kfpColors.kfpEstateProperty;
                                case 3:
                                    return kfpColors.kfpEvaulation;
                                case 4:
                                    return kfpColors.kfpProfitMinus;
                                default:
                                    return kfpColors.kfpOrange; // Fallback color
                            }
                        }
                    ] as any,


                    xaxis: {
                        categories: [
                            keys['estates.calculation.params.estateValue'],
                            keys['estates.calculation.chart.to_pay'],
                            keys['estates.calculation.chart.sum_estate'],
                        ],
                        labels: {
                            show: false,
                        },
                        axisBorder: {
                            show: false
                        },
                    },
                    legend: {
                        show: false
                    },
                    yaxis: {
                        forceNiceScale: true,
                        decimalsInFloat: 0,
                        labels: {
                            offsetX: 10,
                            style: {
                                colors: '#707073',
                                fontSize: '12px',
                                cssClass: "font-semibold",
                            },
                            formatter: (val: number, opts?: any) => {
                                const series = opts?.w?.config?.series || [];
                                const index = opts?.dataPointIndex ?? 0;

                                // Sum values across all series
                                const total = series.reduce((sum: number, currentSeries: any) => {
                                    return sum + (currentSeries.data[index] || 0);
                                }, 0);

                                // Format the total value
                                const formattedTotal = formatNumber(total, 'cs', '1.0-0');
                                return [
                                    `${val}`,
                                    `${formattedTotal} ${this.authService.currency}`
                                ];
                            },
                        },
                    },
                    dataLabels: {
                        enabled: false, // Disable dataLabels
                    },

                    annotations: {
                        xaxis: [{
                            x: 0,
                            borderColor: 'rgba(0,0,0,0.4)',
                            strokeDashArray: 0,
                        }]
                    },
                    tooltip: {
                        enabled: true,
                        y: {
                            formatter: (value: any) => formatNumber(value, 'cs', '1.0-0') + ' ' + this.authService.currency,
                        },
                        x: {
                            formatter: (value: any) => value,
                        },
                    },
                    responsive: {
                        breakpoint: 1000,
                        options: {
                            plotOptions: {
                                bar: {
                                    horizontal: false,
                                },
                            },
                        },
                    },
                });
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    updateSeries(newValue: SeriesLabel[]): void {
        this.logo = this.authService.getLogoPath();
        this.series = newValue;
        this.updateChartHeight();
        const currentObject = this.dataChart.value;
        if (currentObject) {
            const updatedObject = {...currentObject};
            updatedObject.series = newValue[0].series;
            if (newValue[0].colors) {
                updatedObject.colors = newValue[0].colors;
            }

            if (this.logo) {
                updatedObject.chart.background =
                    'linear-gradient(rgba(255,255,255,0.8), rgba(255,255,255,0.8)), url(/' +
                    this.logo +
                    ') no-repeat center; background-size: auto 150px, 150px auto; ';
            }
            this.changeYear(this.year);
        }
    }

    changeYear(year: number) {
        this.year = year;
        const currentObject = this.dataChart.value;
        if (currentObject && this.series) {
            const updatedObject = {...currentObject};
            updatedObject.series = this.series[year]?.series;
            if (updatedObject.yaxis) {
                updatedObject.yaxis.min = this.series[year]?.min;
                updatedObject.yaxis.max = this.series[year]?.max;
            }

            // Precompute colors based on series values
            updatedObject.colors = this.precomputeColors(this.series);

            this.absoluteSeries = this.series[this.year]?.series?.map((seriesItem: any) => ({
                ...seriesItem,
                data: seriesItem.data.map((value: number) => Math.abs(value)),
            }));

            updatedObject.absoluteSeries = this.absoluteSeries;

            this.dataChart.next(updatedObject);
        }
    }

    getChartData() {
        return this.dataChart;
    }

    updateChartHeight(): void {
        if (window.innerWidth > 1425) {
            this.height = (window.innerHeight * 60) / 100;
        } else if (window.innerWidth > 995) {
            this.height = window.innerHeight - 380;
        } else {
            this.height = window.innerHeight - 480;
        }
    }

    precomputeColors(series: SeriesLabel[]): string[] {
        return (series[this.year]?.series ?? []).map((item, seriesIndex) => {
            const value = Array.isArray(item.data) ? item.data.find(val => val !== 0) : item;

            if (typeof value === 'number') {
                switch (seriesIndex) {
                    case 0:
                        return kfpColors.kfpEstatePrice;
                    case 1:
                        return kfpColors.kfpHypoDefault;
                    case 2:
                        return value < 0 ? kfpColors.kfpProfitMinus : kfpColors.kfpEstateProperty;
                    case 3:
                        return kfpColors.kfpEvaulation;
                    case 4:
                        return kfpColors.kfpProfitMinus;
                    default:
                        return kfpColors.kfpOrange; // Fallback color
                }
            } else {
                return kfpColors.kfpOrange; // Fallback color for non-number values
            }
        });
    }

}
